import { createTheme, ThemeOptions } from '@material-ui/core/styles';

import { Theme } from '@/config/types';
import { mergeDeep } from '@/utils/mergeDeep';

const setThemeWithCustomizableValues = (newCustomizableValues: Partial<Theme>): ThemeOptions => {
  const mainDefaultReusableValues = {
    palette: {
      primary: {
        main: '#4dd970',
        contrastText: '#08404d',
      },
      secondary: {
        main: '#08404d',
        contrastText: '#fff',
      },
      danger: {
        main: '#ef5957',
        contrastText: '#ffffff',
      },
      warning: {
        main: '#ffca28',
        contrastText: '#ffffff',
        light: 'rgba(255, 202, 40, 0.15)',
      },
      info: {
        main: '#36a3f7',
        contrastText: '#ffffff',
        light: '#E1F1FD',
      },
      success: {
        main: '#4dd970',
        contrastText: '#ffffff',
        light: 'rgba(73,217,106,0.15)',
      },
      error: {
        main: '#ef5957',
        contrastText: '#ffffff',
        light: 'rgba(239,87,87,0.15)',
      },
      text: {
        accent: '#4dd970',
        active: '#08404d',
        title: '#08404d',
        primary: '#4b5354',
        secondary: '#77787b',
      },
      // additional colors
      dark: {
        main: '#08404d',
        accent: '#4dd970',
        contrastText: '#ffffff',
        imageBackgroundOpacity: 0.85,
      },
      light: {
        main: '#f3f2ef',
        accent: '#4dd970',
        contrastText: '#08404d',
      },
      status: {
        active: '#4dd970',
        approved: '#43b46f',
        analysing: '#36a3f7',
        sent: '#08404d',
        cancelled: '#d8d8d8',
        denied: '#ef5957',
      },
      grey: {
        50: '#fafafa',
        100: '#f5f5f5',
        200: '#eeeeee',
        300: '#e0e0e0',
        400: '#bdbdbd',
        500: '#9e9e9e',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
        A100: '#d5d5d5',
        A200: '#aaaaaa',
        A400: '#303030',
        A700: '#616161',
        background: '#F1F1F1',
        selected: '#d8d8d8',
      },
    },
    background: null,
    logo: null,
    customBorderRadius: {
      button: 25,
      paper: 15,
      chip: 6,
    },
    spacing: [0, 8, 16, 24, 32, 40, 48, 56, 64, 72, 80],
  };

  const targetThemeValues = mergeDeep(
    mainDefaultReusableValues as unknown as Record<string, unknown>,
    newCustomizableValues,
  ) as unknown as Theme;

  targetThemeValues.palette.input = targetThemeValues.palette.input || targetThemeValues.palette.primary;

  const baseThemeConstruction = {
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
      },
    },
    typography: {
      fontFamily: [
        'Raleway',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        '"Open sans"',
        'sans-serif',
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
        '"Segoe UI Symbol"',
      ].join(','),
    },
    overrides: {
      MuiButton: {
        root: {
          borderRadius: targetThemeValues.customBorderRadius.button,
          border: 0,
          textTransform: 'none',
          fontSize: '16px',
          '&.progression': {
            width: 220,
            height: 35,
          },
          '&.progression.xs': {
            border: 'none',
            width: 150,
          },
        },

        contained: {
          backgroundColor: targetThemeValues.palette.minor.main,
          color: targetThemeValues.palette.minor.contrastText,
        },
        sizeLarge: {
          height: '40px',
          fontSize: '18px',
        },
        sizeSmall: {
          paddingInline: '18px',
          height: '30px',
        },
        label: {
          fontWeight: 600,
        },
      },
      MuiToggleButton: {
        label: {
          pointerEvents: 'none',
        },
      },
      MuiFormControl: {
        root: {
          margin: `${targetThemeValues.spacing[1]}px 0`,
        },
      },
      MuiInputBase: {
        input: {
          padding: '10px 0 11px',
        },
      },
      MuiAppBar: {
        root: {
          background: '#fff !important',
        },
      },
      MuiTextField: {
        root: {
          color: targetThemeValues.palette.text.primary,
        },
      },
      MuiSwitch: {
        root: {
          padding: 9,
          top: -9,
          left: -9,
        },
        track: {
          backgroundColor: `${targetThemeValues.palette.grey[100]} !important`,
          opacity: '1 !important',
          height: 20,
          borderRadius: 10,
        },
      },
      MuiInput: {
        root: {
          color: 'inherit',
          position: 'relative',
          transition: 'border-bottom 0.2s ease',
        },
        underline: {
          '&:before': {
            borderBottomWidth: '2px',
            borderBottomColor: 'inherit !important',
          },
          '&:hover:before': {
            borderBottomWidth: '2px',
            borderBottomColor: 'inherit !important',
          },
          '&:after': {
            borderColor: `${targetThemeValues.palette.input.main} !important`,
          },
        },
      },
      MuiChip: {
        root: {
          borderRadius: targetThemeValues.customBorderRadius.chip,
        },
      },
      MuiPaper: {
        rounded: {
          borderRadius: targetThemeValues.customBorderRadius.paper,
        },
        root: {
          backgroundColor: targetThemeValues.background,
          '&.dynamic-dark-opacity-filter': {
            position: 'relative',
            overflow: 'hidden',
            zIndex: 1,
          },
        },
      },
      MuiDialog: {
        paper: {
          padding: '0px',
        },
      },
      MuiGrid: {
        root: {
          '&.stat-results': {
            backgroundColor: '#fff0',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            minHeight: 36,
            cursor: 'pointer',
            padding: '0 10px 0 10px',
            borderRadius: 5,
            whiteSpace: 'nowrap',
            fontWeight: '600',
            '&.total': {
              cursor: 'default',
              display: 'flex',
            },
            '&.total-selected': {
              transition: 'all 0.3s ease',
              cursor: 'default',
              color: targetThemeValues.palette.text.title,
            },
            '&.selected': {
              transition: 'all 0.3s ease',
              backgroundColor: targetThemeValues.palette.grey[200],
              color: targetThemeValues.palette,
            },
            '&.opacity': {
              filter: 'opacity(0.5)',
            },
            '& .flex-icons': {
              display: 'flex',
            },
            '& .stat-strong-value': {
              display: 'flex',
              justifyContent: 'flex-end',
            },
          },
        },
      },
      MuiFormControlLabel: {
        labelPlacementStart: {
          width: '100%',
          marginLeft: '0px',
          marginRight: '-11px',
          flexDirection: 'row-reverse',
          justifyContent: 'space-between',
        },
      },
      MuiInputLabel: {
        root: {
          color: 'inherit',
        },
        shrink: {
          transform: 'translate(0, 1.5px) scale(0.875)',
        },
      },
      MuiFormLabel: {
        root: {
          '&$focused': {
            color: 'inherit',
          },
        },
        focused: {},
      },
      MuiBackdrop: {
        root: {
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
        },
      },
      MuiSelect: {
        select: {
          '&:focus': {
            backgroundColor: 'inherit',
          },
        },
      },
      MuiLinearProgress: {
        root: {
          '&.stat-bar': {
            transition: 'all 0.3s ease',
            backgroundColor: targetThemeValues.palette.grey[200],
            height: 10,
            borderRadius: 5,
            width: '100%',
          },
          '&.stat-bar.thin': {
            transition: 'all 0.3s ease',

            height: 5,
            marginBottom: 5,
          },
        },
      },
      MuiTypography: {
        root: {
          '&.percentage': {
            padding: '0px 0px 0px 12px',
            filter: 'opacity(0.6)',
          },
          '&.header-stats': {
            margintBottom: 30,
          },
          '&.text-overflow': {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontWeight: 600,
          },
        },
      },
    },
    shadows: [
      'none',
      '0px 2px 1px -1px rgba(0,0,0,0.1),0px 0px 1px 0px rgba(0,0,0,0.05),0px 1px 3px 0px rgba(0,0,0,0.08)',
      '0px 3px 1px -2px rgba(0,0,0,0.1),0px 0px 1px 0px rgba(0,0,0,0.05),0px 1px 5px 0px rgba(0,0,0,0.08)',
      '0px 3px 3px -2px rgba(0,0,0,0.1),0px 3px 4px 0px rgba(0,0,0,0.05),0px 1px 8px 0px rgba(0,0,0,0.08)',
      '0px 2px 4px -1px rgba(0,0,0,0.1),0px 4px 5px 0px rgba(0,0,0,0.05),0px 1px 10px 0px rgba(0,0,0,0.08)',
      '0px 3px 5px -1px rgba(0,0,0,0.1),0px 5px 8px 0px rgba(0,0,0,0.05),0px 1px 14px 0px rgba(0,0,0,0.08)',
      '0px 3px 5px -1px rgba(0,0,0,0.1),0px 6px 10px 0px rgba(0,0,0,0.05),0px 1px 18px 0px rgba(0,0,0,0.08)',
      '0px 4px 5px -2px rgba(0,0,0,0.1),0px 7px 10px 1px rgba(0,0,0,0.05),0px 2px 16px 1px rgba(0,0,0,0.08)',
      '0px 5px 5px -3px rgba(0,0,0,0.1),0px 8px 10px 1px rgba(0,0,0,0.05),0px 3px 14px 2px rgba(0,0,0,0.08)',
      '0px 5px 6px -3px rgba(0,0,0,0.1),0px 9px 12px 1px rgba(0,0,0,0.05),0px 3px 16px 2px rgba(0,0,0,0.08)',
      '0px 6px 6px -3px rgba(0,0,0,0.1),0px 10px 14px 1px rgba(0,0,0,0.05),0px 4px 18px 3px rgba(0,0,0,0.08)',
      '0px 6px 7px -4px rgba(0,0,0,0.1),0px 11px 15px 1px rgba(0,0,0,0.05),0px 4px 20px 3px rgba(0,0,0,0.08)',
      '0px 7px 8px -4px rgba(0,0,0,0.1),0px 12px 17px 2px rgba(0,0,0,0.05),0px 5px 22px 4px rgba(0,0,0,0.08)',
      '0px 7px 8px -4px rgba(0,0,0,0.1),0px 13px 19px 2px rgba(0,0,0,0.05),0px 5px 24px 4px rgba(0,0,0,0.08)',
      '0px 7px 9px -4px rgba(0,0,0,0.1),0px 14px 21px 2px rgba(0,0,0,0.05),0px 5px 26px 4px rgba(0,0,0,0.08)',
      '0px 8px 9px -5px rgba(0,0,0,0.1),0px 15px 22px 2px rgba(0,0,0,0.05),0px 6px 28px 5px rgba(0,0,0,0.08)',
      '0px 8px 10px -5px rgba(0,0,0,0.1),0px 16px 24px 2px rgba(0,0,0,0.05),0px 6px 30px 5px rgba(0,0,0,0.08)',
      '0px 8px 11px -5px rgba(0,0,0,0.1),0px 17px 26px 2px rgba(0,0,0,0.05),0px 6px 32px 5px rgba(0,0,0,0.08)',
      '0px 9px 11px -5px rgba(0,0,0,0.1),0px 18px 28px 2px rgba(0,0,0,0.05),0px 7px 34px 6px rgba(0,0,0,0.08)',
      '0px 9px 12px -6px rgba(0,0,0,0.1),0px 19px 29px 2px rgba(0,0,0,0.05),0px 7px 36px 6px rgba(0,0,0,0.08)',
      '0px 10px 13px -6px rgba(0,0,0,0.1),0px 20px 31px 3px rgba(0,0,0,0.05),0px 8px 38px 7px rgba(0,0,0,0.08)',
      '0px 10px 13px -6px rgba(0,0,0,0.1),0px 21px 33px 3px rgba(0,0,0,0.05),0px 8px 40px 7px rgba(0,0,0,0.08)',
      '0px 10px 14px -6px rgba(0,0,0,0.1),0px 22px 35px 3px rgba(0,0,0,0.05),0px 8px 42px 7px rgba(0,0,0,0.08)',
      '0px 11px 14px -7px rgba(0,0,0,0.1),0px 23px 36px 3px rgba(0,0,0,0.05),0px 9px 44px 8px rgba(0,0,0,0.08)',
      '0px 11px 15px -7px rgba(0,0,0,0.1),0px 24px 38px 3px rgba(0,0,0,0.05),0px 9px 46px 8px rgba(0,0,0,0.08)',
    ],
  };

  return mergeDeep(
    baseThemeConstruction,
    targetThemeValues as unknown as Record<string, unknown>,
  ) as unknown as ThemeOptions;
};

const createCustomTheme = (newCustomizableValues: Partial<Theme>) => {
  return createTheme(setThemeWithCustomizableValues(newCustomizableValues));
};

export default createCustomTheme;
